/**
 * Admin endpoints for instruments — the canonical, exchange-side instrument
 * master. Backed by the real backend `/admin/instruments`.
 *
 * Identity is a surrogate UUID; classification comes from the segment
 * (gives exchange + asset_class). Equities carry an ISIN; derivatives
 * (futures/options) don't — they're identified by their contract spec.
 * `broker_mapping_count` is how many brokers have a token mapped to this
 * instrument.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

export const adminInstrumentSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  name: z.string().nullable(),
  isin: z.string().nullable(),
  segment_id: z.string(),
  segment_display_name: z.string().nullable(),
  asset_class: z.string().nullable(),
  exchange_id: z.string().nullable(),
  currency_id: z.string(),
  tick_size: z.number(),
  lot_size: z.number().int(),
  contract_multiplier: z.number(),
  underlying_symbol: z.string().nullable(),
  expiry: z.string().nullable(),
  strike: z.number().nullable(),
  option_type: z.string().nullable(),
  status: z.string(),
  broker_mapping_count: z.number().int(),
  created_at: z.string(),
});
export const adminInstrumentListSchema = z.array(adminInstrumentSchema);
export type AdminInstrument = z.infer<typeof adminInstrumentSchema>;

export const brokerInstrumentMappingSchema = z.object({
  broker_id: z.string(),
  broker_token: z.string(),
  broker_symbol: z.string().nullable(),
  broker_segment_code: z.string().nullable(),
  routing_config: z.record(z.unknown()),
  status: z.string(),
});
export const adminInstrumentDetailSchema = adminInstrumentSchema.extend({
  updated_at: z.string(),
  mappings: z.array(brokerInstrumentMappingSchema),
  // Active OPTION rows naming this instrument as their underlying — drives
  // the "View option chain →" link on an underlying's detail page (options
  // are hidden from the main list, surfaced here + on the Option Chains page).
  option_count: z.number().int().optional().default(0),
});
export type AdminInstrumentDetail = z.infer<typeof adminInstrumentDetailSchema>;
export type BrokerInstrumentMapping = z.infer<typeof brokerInstrumentMappingSchema>;

export const getInstrument = (
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<AdminInstrumentDetail>> =>
  get(`/admin/instruments/${encodeURIComponent(id)}`, adminInstrumentDetailSchema, {
    signal,
  }) as Promise<ApiResult<AdminInstrumentDetail>>;

export interface ListInstrumentsQuery {
  exchange_id?: string;
  segment_id?: string;
  status?: string;
  kind?: 'cash' | 'future' | 'option';
  q?: string;
  /** Include past-expiry + non-active rows. Default = live tradeable universe. */
  include_expired?: boolean;
  limit?: number;
  offset?: number;
}

export const listInstruments = (
  query: ListInstrumentsQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<AdminInstrument[]>> =>
  get('/admin/instruments', adminInstrumentListSchema, {
    signal,
    query: {
      exchange_id: query.exchange_id,
      segment_id: query.segment_id,
      status: query.status,
      kind: query.kind,
      q: query.q,
      include_expired: query.include_expired,
      limit: query.limit,
      offset: query.offset,
    },
  }) as Promise<ApiResult<AdminInstrument[]>>;

/** Paged envelope read off `X-Total-Count`. Use this when you need an
 * honest "X of Y" pagination counter. Falls back to `items.length` when
 * the header is absent (defensive — server should always set it). */
export interface PagedInstruments {
  items: AdminInstrument[];
  total: number;
}

export const listInstrumentsPaged = async (
  query: ListInstrumentsQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<PagedInstruments>> => {
  const r = await listInstruments(query, signal);
  const headerTotal = r.meta.headers?.['x-total-count'];
  const total =
    headerTotal !== undefined && headerTotal !== '' ? Number(headerTotal) : r.data.length;
  return {
    data: { items: r.data, total: Number.isFinite(total) ? total : r.data.length },
    meta: r.meta,
  };
};

/** Derived: an instrument is expired when its expiry date has passed. */
export const isExpired = (i: AdminInstrument): boolean =>
  i.expiry !== null && new Date(i.expiry).getTime() < Date.now();

// ── Candles (canonical OHLCV, venue-tagged) ───────────────────────────
export const candleSchema = z.object({
  ts: z.string(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
  volume: z.number(),
});
export const instrumentCandlesSchema = z.object({
  instrument_id: z.string(),
  venue_id: z.string().nullable(),
  timeframe: z.string(),
  count: z.number().int(),
  candles: z.array(candleSchema),
});
export type Candle = z.infer<typeof candleSchema>;
export type InstrumentCandles = z.infer<typeof instrumentCandlesSchema>;

export const getInstrumentCandles = (
  id: string,
  query: { timeframe?: string; venue_id?: string; limit?: number; source?: 'broker' | 'db' } = {},
  signal?: AbortSignal,
): Promise<ApiResult<InstrumentCandles>> =>
  get(
    `/admin/instruments/${encodeURIComponent(id)}/candles`,
    instrumentCandlesSchema,
    {
      signal,
      query: {
        timeframe: query.timeframe,
        venue_id: query.venue_id,
        limit: query.limit,
        source: query.source,
      },
    },
  ) as Promise<ApiResult<InstrumentCandles>>;
